/**
 * GroupTree
 *
 * Recursive render of the admin's user-groups tree, with the assigned agents
 * listed under each group header. Agents without a group fall into a final
 * "(Sin grupo)" bucket at the bottom.
 *
 * Inputs come from `useGroups` (groups) and `useAgents` (agents). Both are
 * already cached via TanStack Query, so reading them is free here.
 *
 * Pass-through props (onAgentClick / onConnect / onConsole / onDetails) are
 * forwarded to each `<AgentCard>` so behavior matches the flat list.
 */

import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { Agent } from '@/types/agent';
import type { UserGroup } from '@/types/groups';
import { AgentCard } from '@/components/agents/AgentCard';
import { GroupHeader } from './GroupHeader';

interface GroupTreeProps {
  groups: UserGroup[];
  agents: Agent[];
  onAgentClick?: (agentId: string) => void;
  onConnect?: (agentId: string) => void;
  onConsole?: (agentId: string) => void;
  onDetails?: (agentId: string) => void;
  viewMode?: 'grid' | 'list' | 'listao';
}

export const GroupTree: React.FC<GroupTreeProps> = ({
  groups,
  agents,
  onAgentClick,
  onConnect,
  onConsole,
  onDetails,
  viewMode = 'grid',
}) => {
  // Build assignment lookup once per render (cheap; small N).
  const agentsByGroupId = useMemo(() => {
    const map = new Map<string, Agent[]>();
    const unassigned: Agent[] = [];
    for (const a of agents) {
      if (a.groupId) {
        const arr = map.get(a.groupId) ?? [];
        arr.push(a);
        map.set(a.groupId, arr);
      } else {
        unassigned.push(a);
      }
    }
    return { map, unassigned };
  }, [agents]);

  // Render one group (header + subgroups + agents).
  const renderGroup = (group: UserGroup, depth: number) => {
    const groupAgents = agentsByGroupId.map.get(group.id) ?? [];
    // Subgroups: use the embedded list (populated when ?include=subgroups).
    return (
      <Box key={group.id} data-testid={`group-node-${group.id}`}>
        <GroupHeader group={group} depth={depth} />
        {group.subgroups && group.subgroups.length > 0 && (
          <Box sx={{ pl: 4 }}>
            {group.subgroups.map((sg) => (
              <Box
                key={sg.id}
                data-testid={`subgroup-node-${sg.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 1,
                  py: 0.25,
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {sg.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {groupAgents.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pl: 4,
              pr: 1,
              py: 1,
            }}
          >
            {groupAgents.map((a) => (
              <AgentCard
                key={a.agentId}
                agent={a}
                groups={groups}
                onClick={() => onAgentClick?.(a.agentId)}
                onConnect={() => onConnect?.(a.agentId)}
                onConsole={() => onConsole?.(a.agentId)}
                onDetails={() => onDetails?.(a.agentId)}
                viewMode={viewMode}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box data-testid="group-tree">
      {groups.map((g) => renderGroup(g, 0))}

      {agentsByGroupId.unassigned.length > 0 && (
        <Box sx={{ mt: 2 }} data-testid="group-tree-unassigned">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderTop: 1,
              borderColor: 'divider',
              pt: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              (Sin grupo)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {agentsByGroupId.unassigned.length} agente
              {agentsByGroupId.unassigned.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            {agentsByGroupId.unassigned.map((a) => (
              <AgentCard
                key={a.agentId}
                agent={a}
                groups={groups}
                onClick={() => onAgentClick?.(a.agentId)}
                onConnect={() => onConnect?.(a.agentId)}
                onConsole={() => onConsole?.(a.agentId)}
                onDetails={() => onDetails?.(a.agentId)}
                viewMode={viewMode}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
